import { useEffect, useState } from 'react';
import { Sparkles, CloudOff, MessageSquareQuote, Loader } from 'lucide-react';
import { supabase } from '../lib/supabase';
import AiReviewReport, { type AiReviewFile, type AiReviewItem, type AiReviewStatus } from './AiReviewReport';

interface ReviewRow {
  status: 'completed' | 'failed';
  overall: AiReviewStatus | null;
  items: AiReviewItem[];
  files: AiReviewFile[];
  seller_justification: string | null;
  created_at: string;
}

export default function ContractAiReviewPanel({ contractId }: { contractId: string }) {
  const [row, setRow] = useState<ReviewRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    setRow(null);
    supabase
      .from('contract_ai_reviews')
      .select('status, overall, items, files, seller_justification, created_at')
      .eq('contract_id', contractId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) setLoadError(true);
        else setRow(data as ReviewRow | null);
        setLoading(false);
      });
    return () => { active = false; };
  }, [contractId]);

  if (!loading && !loadError && !row) return null;

  return (
    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
        <Sparkles className="w-4 h-4 text-sky-600" />
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex-1">Revisão da IA</h3>
        {row && (
          <span className="text-xs text-gray-400">
            {new Date(row.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
          </span>
        )}
      </div>
      <div className="p-4 space-y-3">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader className="w-4 h-4 animate-spin" /> Carregando revisão...
          </div>
        )}
        {loadError && <p className="text-sm text-red-600">Não foi possível carregar a revisão da IA.</p>}

        {row?.status === 'failed' && (
          <div className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50">
            <CloudOff className="w-4 h-4 text-gray-500 mt-0.5" />
            <p className="text-sm text-gray-700">Sem revisão automática: a análise não pôde ser concluída. Confira os documentos manualmente.</p>
          </div>
        )}

        {row?.status === 'completed' && row.overall && (
          <AiReviewReport overall={row.overall} items={row.items || []} files={row.files || []} />
        )}

        {row?.seller_justification && (
          <div className="p-3 rounded-xl border border-amber-200 bg-amber-50">
            <div className="flex items-center gap-1.5 mb-1">
              <MessageSquareQuote className="w-4 h-4 text-amber-700" />
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Justificativa do vendedor</p>
            </div>
            <p className="text-sm text-amber-900 whitespace-pre-wrap leading-relaxed">{row.seller_justification}</p>
          </div>
        )}
      </div>
    </section>
  );
}
